import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, X, Loader2, CalendarPlus, Wallet, CalendarClock, Ban } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money, fmtDate, fmtTimeRange } from "@/lib/format";
import UnifiedPaymentModal, { invalidatePayments } from "@/components/pos/UnifiedPaymentModal";
import RescheduleModal from "@/components/pos/RescheduleModal";
import AddAppointmentModal from "@/components/pos/AddAppointmentModal";

const PAY_TONE = { paid: "text-[#5C8A72]", partial: "text-[#B5794E]", deposit: "text-[#B5794E]" };

function BookingRow({ r, onPay, onResched, onCancel }) {
  const owed = r.balance_due > 0 && r.status !== "cancelled";
  return (
    <div className="eyw-card p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`pos-record-row-${r.id}`}>
      <div className="min-w-0">
        <p className="font-medium text-[#1A242B]">{r.title}{r.status === "cancelled" && <span className="ml-2 text-xs text-red-500">Cancelled</span>}</p>
        <p className="text-xs text-[#4A5D6B]">{fmtDate(r.date)} {fmtTimeRange(r.time)}{r.enhancer_name ? ` · ${r.enhancer_name}` : ""}{r.location ? ` · ${r.location}` : ""}</p>
        <p className="text-xs mt-1">Total {money(r.total_price)} · Paid {money(r.amount_paid)} · <span className={owed ? "text-[#B5794E] font-semibold" : "text-[#5C8A72]"}>Due {money(r.balance_due)}</span> · <span className={PAY_TONE[r.payment_status] || "text-[#8FA89B]"}>{(r.payment_status || "unpaid").replace("_", " ")}</span></p>
      </div>
      <div className="flex gap-1.5">
        {owed && <button onClick={() => onPay(r)} data-testid={`pos-record-pay-${r.id}`} className="eyw-btn !py-1.5 !px-3 text-xs"><Wallet size={13} /> Take payment</button>}
        {r.kind === "booking" && r.status !== "cancelled" && onResched && <>
          <button onClick={() => onResched(r)} data-testid={`pos-record-resched-${r.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs"><CalendarClock size={13} /> Reschedule</button>
          <button onClick={() => onCancel(r)} data-testid={`pos-record-cancel-${r.id}`} className="eyw-btn-ghost !py-1.5 !px-3 text-xs text-red-600"><Ban size={13} /> Cancel</button>
        </>}
      </div>
    </div>
  );
}

function ClientRecord({ person, onClose }) {
  const qc = useQueryClient();
  const qs = new URLSearchParams({ client_id: person.client_id || "", user_id: person.user_id || "" }).toString();
  const { data, isLoading, refetch } = useQuery({ queryKey: ["pos-u-person", qs], queryFn: () => api.get(`/pos/u/person?${qs}`).then((r) => r.data) });
  const [pay, setPay] = useState(null);
  const [resched, setResched] = useState(null);
  const [adding, setAdding] = useState(false);
  const cancel = async (r) => {
    if (!window.confirm(`Cancel ${r.title} on ${fmtDate(r.date)}?`)) return;
    try { await api.put(`/pos/appointments/${r.id}/status`, { status: "cancelled" }); toast.success("Appointment cancelled"); invalidatePayments(qc); refetch(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const row = (r) => <BookingRow key={`${r.kind}-${r.id}`} r={r} onPay={setPay} onResched={setResched} onCancel={cancel} />;
  return (
    <div className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-sm flex justify-end" data-testid="pos-client-record">
      <div className="w-full max-w-2xl h-full bg-[#F9F7F3] overflow-y-auto p-6 sm:p-8">
        <div className="flex justify-between items-start gap-3">
          <div>
            <p className="eyw-label">Client record</p>
            <h2 className="font-serif text-2xl text-[#1A242B]" data-testid="pos-client-record-name">{person.name}</h2>
            <p className="text-sm text-[#4A5D6B]">{[person.email, person.phone].filter(Boolean).join(" · ")}</p>
          </div>
          <button onClick={onClose} data-testid="pos-client-record-close"><X size={20} /></button>
        </div>
        <div className="flex flex-wrap gap-3 mt-4 items-center">
          <span className="rounded-full bg-[#B5794E]/10 text-[#B5794E] px-3 py-1 text-sm" data-testid="pos-client-record-outstanding">Outstanding {money(data?.outstanding || 0)}</span>
          <button onClick={() => setAdding(true)} data-testid="pos-client-record-new-appt" className="eyw-btn !py-2 text-sm"><CalendarPlus size={15} /> New appointment</button>
        </div>
        {isLoading ? <Loader2 className="animate-spin mt-8 text-[#144F75]" /> : (
          <>
            <h3 className="font-serif text-lg mt-7 mb-2">Upcoming</h3>
            <div className="space-y-2" data-testid="pos-client-record-upcoming">{data.upcoming.length ? data.upcoming.map(row) : <p className="text-sm text-[#8FA89B]">No upcoming appointments.</p>}</div>
            <h3 className="font-serif text-lg mt-7 mb-2">Past</h3>
            <div className="space-y-2" data-testid="pos-client-record-past">{data.past.length ? data.past.map(row) : <p className="text-sm text-[#8FA89B]">No past appointments yet.</p>}</div>
          </>
        )}
      </div>
      {pay && <UnifiedPaymentModal kind={pay.kind} refId={pay.id} onClose={() => { setPay(null); refetch(); }} />}
      {resched && <RescheduleModal appt={{ ...resched, start_time: resched.time }} onClose={() => setResched(null)} onDone={() => { invalidatePayments(qc); refetch(); }} />}
      {adding && <AddAppointmentModal onClose={() => setAdding(false)} onCreated={() => { setAdding(false); invalidatePayments(qc); refetch(); }} />}
    </div>
  );
}

// Prominent client/booking search at the top of the register (name, email or phone).
export default function UnifiedSearch() {
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(null);
  const box = useRef(null);
  useEffect(() => { const t = setTimeout(() => setTerm(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const { data = [], isFetching } = useQuery({ queryKey: ["pos-u-search", term], queryFn: () => api.get(`/pos/u/search?q=${encodeURIComponent(term)}`).then((r) => r.data), enabled: term.length >= 2 });
  return (
    <div className="relative mb-6" ref={box} data-testid="pos-unified-search">
      <div className="flex items-center gap-3 rounded-2xl bg-white border-2 border-[#144F75]/15 focus-within:border-[#144F75] px-4 py-3 shadow-sm transition-colors">
        <Search size={20} className="text-[#144F75]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a client or booking — name, email or phone" data-testid="pos-search-input" className="flex-1 bg-transparent outline-none text-base" />
        {isFetching && <Loader2 size={16} className="animate-spin text-[#8FA89B]" />}
        {q && <button onClick={() => setQ("")} data-testid="pos-search-clear"><X size={16} className="text-[#8FA89B]" /></button>}
      </div>
      {term.length >= 2 && q && (
        <div className="absolute z-40 left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-[#E5E0D8] max-h-[420px] overflow-y-auto" data-testid="pos-search-results">
          {data.length === 0 && !isFetching ? <p className="p-4 text-sm text-[#8FA89B]" data-testid="pos-search-empty">No clients match "{term}".</p> : data.map((p, i) => (
            <button key={`${p.client_id}-${p.user_id}`} onClick={() => { setOpen(p); setQ(""); }} data-testid={`pos-search-result-${i}`}
              className="w-full text-left px-4 py-3 border-b border-[#F1EEE8] hover:bg-[#144F75]/[0.04] flex justify-between gap-3">
              <span className="min-w-0"><span className="block font-medium text-[#1A242B] truncate">{p.name}</span><span className="block text-xs text-[#8FA89B] truncate">{[p.email, p.phone].filter(Boolean).join(" · ")}</span></span>
              <span className="text-xs text-right shrink-0 text-[#4A5D6B]">{p.upcoming} upcoming · {p.past} past{p.outstanding > 0 && <span className="block text-[#B5794E] font-semibold">{money(p.outstanding)} due</span>}</span>
            </button>
          ))}
        </div>
      )}
      {open && <ClientRecord person={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
